import React, { useState, useEffect } from 'react';
import './ChallengeDetail.css';

function ChallengeDetail({ challengeId, onNavigate }) {
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [userIsParticipant, setUserIsParticipant] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallengeDetails();
    const interval = setInterval(fetchChallengeDetails, 30000);
    return () => clearInterval(interval);
  }, [challengeId]);

  const fetchChallengeDetails = async () => {
    try {
      const response = await fetch(`/backend/api/challenges/details.php?challenge_id=${challengeId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChallenge(data.challenge);
          setLeaderboard(data.leaderboard);
          setTaskTypes(data.task_types);
          setRecentActivity(data.recent_activity);
          setUserIsParticipant(data.user_is_participant);
        }
      }
    } catch (error) {
      console.error('Error fetching challenge details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      const response = await fetch('/backend/api/challenges/join.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchChallengeDetails();
      } else {
        alert(data.error || 'Failed to join challenge');
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
      alert('Failed to join challenge');
    }
  };

  if (loading) {
    return <div className="challenge-detail-loading">Loading challenge...</div>;
  }

  if (!challenge) {
    return <div className="challenge-detail-error">Challenge not found</div>;
  }

  const getTimeRemaining = (deadline) => {
    const now = new Date().getTime();
    const deadlineTime = new Date(deadline).getTime();
    const diff = deadlineTime - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="challenge-detail-page">
      <button className="back-btn-detail" onClick={() => onNavigate('challenges')}>
        ← Back to Challenges
      </button>
      
      <div className="challenge-detail-header">
        <h1>{challenge.title}</h1>
        <span className={`status-badge-detail ${challenge.status}`}>
          {challenge.status === 'accepting' && '⏰ Open'}
          {challenge.status === 'active' && '🔥 Active'}
          {challenge.status === 'completed' && '✓ Completed'}
        </span>
      </div>
      
      {challenge.description && (
        <p className="challenge-description-detail">{challenge.description}</p>
      )}
      
      <div className="challenge-detail-grid">
        <div className="detail-main-col">
          <div className="detail-card">
            <h3>🏆 Leaderboard</h3>
            {leaderboard.length === 0 ? (
              <p className="empty-text">No participants yet</p>
            ) : (
              <div className="leaderboard-table">
                {leaderboard.map((participant) => (
                  <div 
                    key={participant.user_id} 
                    className={`leaderboard-row ${participant.is_current_user ? 'current-user' : ''} ${participant.rank <= 3 ? `rank-${participant.rank}` : ''}`}
                  >
                    <span className="rank-col">
                      {participant.rank <= 3 ? (
                        participant.rank === 1 ? '🥇' : participant.rank === 2 ? '🥈' : '🥉'
                      ) : (
                        `#${participant.rank}`
                      )}
                    </span>
                    <span className="name-col">{participant.first_name} {participant.last_name}</span>
                    <span className="score-col">{participant.score} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {recentActivity.length > 0 && (
            <div className="detail-card">
              <h3>📊 Recent Activity</h3>
              <div className="activity-list">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <span className="activity-user">{activity.first_name} {activity.last_name}</span>
                    <span className="activity-desc">
                      completed "{activity.task_name}" with {activity.contact_first_name} {activity.contact_last_name}
                    </span>
                    <span className="activity-points">+{activity.points_earned} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="detail-sidebar-col">
          <div className="detail-card">
            <h3>ℹ️ Challenge Info</h3>
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">Created by:</span>
                <span className="info-value">{challenge.creator_first_name} {challenge.creator_last_name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">
                  {challenge.challenge_type === 'count_tasks' ? `First to ${challenge.target_number}` : 'Time-based'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Participants:</span>
                <span className="info-value">{leaderboard.length}</span>
              </div>
              {challenge.prize && (
                <div className="info-item">
                  <span className="info-label">Prize:</span>
                  <span className="info-value prize-value">🏆 {challenge.prize}</span>
                </div>
              )}
              {challenge.status === 'completed' && challenge.winner_first_name && (
                <div className="info-item">
                  <span className="info-label">Winner:</span>
                  <span className="info-value winner-value">{challenge.winner_first_name} {challenge.winner_last_name}</span>
                </div>
              )}
            </div>
          </div>
          
          {challenge.status === 'accepting' && (
            <div className="detail-card accepting-card">
              <h3>⏰ Time to Join</h3>
              <div className="countdown-display">{getTimeRemaining(challenge.acceptance_deadline)}</div>
              {!userIsParticipant && (
                <button className="join-challenge-btn-detail" onClick={handleJoin}>
                  Join Challenge
                </button>
              )}
              {userIsParticipant && (
                <div className="joined-badge">✓ You're In!</div>
              )}
            </div>
          )}
          
          <div className="detail-card">
            <h3>📋 Tasks That Count</h3>
            <div className="task-types-list">
              {taskTypes.map((task, index) => (
                <div key={index} className="task-type-item">
                  <span className="task-stage-badge">{task.contact_stage}</span>
                  <span className="task-name">{task.task_name}</span>
                  <span className="task-points">{task.points_per_completion} pt{task.points_per_completion !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChallengeDetail;

